import { ASTNode, FormulaError, FormulaErrorType, FormulaValue } from './types';
import { FunctionRegistry } from './functions';

export class Evaluator {
    private readonly variables: Map<string, number | number[] | string | null>;
    private readonly registry: FunctionRegistry;

    constructor(variables: Map<string, number | number[] | string | null>, registry: FunctionRegistry) {
        this.variables = variables;
        this.registry = registry;
    }

    evaluate(node: ASTNode): FormulaValue {
        switch (node.type) {
            case 'number':
                return node.value;
            case 'string':
                return node.value;
            case 'boolean':
                return node.value;
            case 'array':
                return node.values;
            case 'identifier':
                return this.resolveVariable(node.name);
            case 'unaryOp':
                return this.evaluateUnary(node.op, node.operand);
            case 'binaryOp':
                return this.evaluateBinary(node.op, node.left, node.right);
            case 'percent':
                return this.evaluatePercent(node.operand);
            case 'functionCall':
                return this.evaluateFunction(node.name, node.args);
            default:
                return 0;
        }
    }

    private resolveVariable(name: string): FormulaValue {
        if (!this.variables.has(name)) {
            throw new FormulaError(FormulaErrorType.NAME, `Unknown variable: ${name}`);
        }
        const value = this.variables.get(name);
        if (value === null || value === undefined) return 0; // evaluateNullToZero
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') {
            if (value === '') return value;
            const parsed = Number(value);
            return isNaN(parsed) ? value : parsed;
        }
        return value;
    }

    private evaluateUnary(op: '+' | '-', operand: ASTNode): FormulaValue {
        const val = this.evaluate(operand);

        if (Array.isArray(val)) {
            return val.map(v => op === '-' ? -v : v);
        }

        const num = this.toNumber(val);
        return op === '-' ? -num : num;
    }

    private evaluateBinary(op: string, left: ASTNode, right: ASTNode): FormulaValue {
        const leftVal = this.evaluate(left);
        const rightVal = this.evaluate(right);

        // String concatenation
        if (op === '&') {
            return this.toString(leftVal) + this.toString(rightVal);
        }

        // Array arithmetic
        if (Array.isArray(leftVal) || Array.isArray(rightVal)) {
            return this.arrayBinaryOp(op, leftVal, rightVal);
        }

        // Comparison operators - preserve types for correct string comparison
        if (op === '=' || op === '<>' || op === '<' || op === '<=' || op === '>' || op === '>=') {
            return this.evaluateComparison(op, leftVal, rightVal);
        }

        const l = this.toNumber(leftVal);
        const r = this.toNumber(rightVal);

        switch (op) {
            case '+': return smartRound(l + r);
            case '-': return smartRound(l - r);
            case '*': return smartRound(l * r);
            case '/':
                if (r === 0) return 0;
                return smartRound(l / r);
            case '^': return smartRound(Math.pow(l, r));
            default: return 0;
        }
    }

    private evaluateComparison(op: string, leftVal: FormulaValue, rightVal: FormulaValue): boolean {
        // If either operand is a string, compare as strings (case-insensitive)
        if (typeof leftVal === 'string' || typeof rightVal === 'string') {
            const l = this.toString(leftVal).toLowerCase();
            const r = this.toString(rightVal).toLowerCase();
            switch (op) {
                case '=': return l === r;
                case '<>': return l !== r;
                case '<': return l < r;
                case '<=': return l <= r;
                case '>': return l > r;
                case '>=': return l >= r;
                default: return false;
            }
        }

        // Otherwise compare as numbers
        const l = this.toNumber(leftVal);
        const r = this.toNumber(rightVal);
        switch (op) {
            case '=': return l === r;
            case '<>': return l !== r;
            case '<': return l < r;
            case '<=': return l <= r;
            case '>': return l > r;
            case '>=': return l >= r;
            default: return false;
        }
    }

    private arrayBinaryOp(op: string, leftVal: FormulaValue, rightVal: FormulaValue): FormulaValue {
        const leftArr = Array.isArray(leftVal) ? leftVal : null;
        const rightArr = Array.isArray(rightVal) ? rightVal : null;

        if (leftArr && rightArr) {
            // Element-wise operation
            const len = Math.max(leftArr.length, rightArr.length);
            const result: number[] = [];
            for (let i = 0; i < len; i++) {
                const l = i < leftArr.length ? leftArr[i] : 0;
                const r = i < rightArr.length ? rightArr[i] : 0;
                result.push(this.scalarBinaryOp(op, l, r));
            }
            return result;
        }

        if (leftArr) {
            const r = this.toNumber(rightVal);
            return leftArr.map(l => this.scalarBinaryOp(op, l, r));
        }

        if (rightArr) {
            const l = this.toNumber(leftVal);
            return rightArr.map(r => this.scalarBinaryOp(op, l, r));
        }

        return 0;
    }

    private scalarBinaryOp(op: string, l: number, r: number): number {
        switch (op) {
            case '+': return smartRound(l + r);
            case '-': return smartRound(l - r);
            case '*': return smartRound(l * r);
            case '/': return r === 0 ? 0 : smartRound(l / r);
            case '^': return smartRound(Math.pow(l, r));
            default: return 0;
        }
    }

    private evaluatePercent(operand: ASTNode): FormulaValue {
        const val = this.evaluate(operand);
        if (Array.isArray(val)) {
            return val.map(v => v / 100);
        }
        return this.toNumber(val) / 100;
    }

    private evaluateFunction(name: string, argNodes: ASTNode[]): FormulaValue {
        const fn = this.registry.resolve(name);
        if (!fn) {
            throw new FormulaError(FormulaErrorType.NAME, `Unknown function: ${name}`);
        }

        // Evaluate arguments
        const args = argNodes.map(arg => this.evaluate(arg));
        return fn(...args);
    }

    toNumber(val: FormulaValue): number {
        if (val === null || val === undefined) return 0;
        if (typeof val === 'number') return isNaN(val) ? 0 : val;
        if (typeof val === 'boolean') return val ? 1 : 0;
        if (typeof val === 'string') {
            if (val === '') return 0;
            const n = Number(val.replace(',', '.'));
            return isNaN(n) ? 0 : n;
        }
        if (Array.isArray(val)) {
            // When an array is used in a scalar context, return sum
            return val.reduce((a, b) => a + b, 0);
        }
        return 0;
    }

    private toString(val: FormulaValue): string {
        if (val === null || val === undefined) return '';
        if (typeof val === 'boolean') return val ? 'TRUE' : 'FALSE';
        if (Array.isArray(val)) return val.join(',');
        return String(val);
    }
}

// Smart rounding to avoid floating-point artifacts (e.g., 0.1 + 0.2 = 0.30000000000000004)
export function smartRound(value: number): number {
    if (!isFinite(value)) return value;
    return Math.round(value * 1e10) / 1e10;
}
